/**
 * Render-related widgets:
 * - PrettyRenderBlockWidget: full-line ::render replacement (L0)
 * - PrettyRenderInlineWidget: compact inline pill for ::render within text (L0)
 */

import { WidgetType } from "@codemirror/view";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";
import {
    appendInlineStatus,
    attachSyntaxEditInteractions,
    createHoverDetail,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTextSpan,
} from "../shared/widgets";

/* L0 Block render widget — same visual style as PrettyDefineWidget (for full-line ::render) */
export class PrettyRenderBlockWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly targetKind: string,
        readonly summary: string,
        readonly isImported: boolean = false,
        readonly pluginName: string | null = null,
        readonly comment: string | null = null,
    ) {
        super();
    }
    eq(other: PrettyRenderBlockWidget) {
        return (
            other.defId === this.defId &&
            other.targetKind === this.targetKind &&
            other.summary === this.summary &&
            other.isImported === this.isImported &&
            other.pluginName === this.pluginName &&
            other.comment === this.comment
        );
    }
    toDOM(): HTMLElement {
        const arrow = createSyntaxBadge("R", {
            sub: "D",
            sup: this.isImported ? "M" : undefined,
        });
        arrow.classList.add("cm-magic-render-pretty-block-arrow");

        const primary = [createTextSpan("cm-magic-render-pretty-block-id", this.defId)];
        if (this.pluginName) {
            primary.push(
                createTextSpan("cm-magic-render-pretty-plugin", this.pluginName),
            );
        }

        const wrapper = createPrettySyntaxShell({
            wrapperClass: "cm-magic-render-pretty-block",
            badge: arrow,
            primary,
            detail: createHoverDetail("cm-magic-render-pretty-detail", [
                createTextSpan("cm-magic-hover-kind cm-magic-render-pretty-kind", this.targetKind),
                this.comment
                    ? createTextSpan(
                        "cm-magic-hover-summary cm-magic-render-pretty-comment",
                        this.comment,
                    )
                    : null,
                this.summary
                    ? createTextSpan(
                        "cm-magic-hover-summary cm-magic-render-pretty-summary",
                        this.summary,
                    )
                    : null,
            ]),
        });
        wrapper.dataset.magicNavTarget = this.defId;
        wrapper.dataset.magicNavKind = "render";
        attachSyntaxEditInteractions(wrapper);
        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

/* L0 Inline render widget — compact pill for inline ::render within text */
export class PrettyRenderInlineWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly targetKind: string,
        readonly summary: string,
        readonly isImported: boolean = false,
        readonly status: ExecutionStatus = "idle",
        readonly duration: number | null = null,
        readonly pluginName: string | null = null,
        readonly comment: string | null = null,
    ) {
        super();
    }
    eq(other: PrettyRenderInlineWidget) {
        return (
            other.defId === this.defId &&
            other.targetKind === this.targetKind &&
            other.summary === this.summary &&
            other.isImported === this.isImported &&
            other.status === this.status &&
            other.duration === this.duration &&
            other.pluginName === this.pluginName &&
            other.comment === this.comment
        );
    }
    toDOM(): HTMLElement {
        const arrow = createSyntaxBadge("R", {
            sub: "D",
            sup: this.isImported ? "M" : undefined,
        });
        arrow.classList.add("cm-magic-render-pretty-inline-arrow");

        const primary = [createTextSpan("cm-magic-render-pretty-inline-id", this.defId)];
        if (this.pluginName) {
            primary.push(
                createTextSpan("cm-magic-render-pretty-plugin", this.pluginName),
            );
        }

        const wrapper = createPrettySyntaxShell({
            wrapperClass: "cm-magic-render-pretty-inline",
            badge: arrow,
            primary,
            detail: createHoverDetail("cm-magic-render-pretty-detail", [
                createTextSpan("cm-magic-hover-kind cm-magic-render-pretty-kind", this.targetKind),
                this.comment
                    ? createTextSpan(
                        "cm-magic-hover-summary cm-magic-render-pretty-comment",
                        this.comment,
                    )
                    : null,
                this.summary
                    ? createTextSpan(
                        "cm-magic-hover-summary cm-magic-render-pretty-summary",
                        this.summary,
                    )
                    : null,
            ]),
        });
        appendInlineStatus(wrapper, this.status, this.duration);
        wrapper.dataset.magicNavTarget = this.defId;
        wrapper.dataset.magicNavKind = "render";
        attachSyntaxEditInteractions(wrapper);
        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}
